import { Parser } from "htmlparser2";

import { isE164 } from "../messaging/providers/sms/twilio-content-formats";
import { isLocalizableUrl } from "./normalize";

/**
 * What a translated value may never carry. The render writes a `text` slot raw, so a
 * cell reaches the delivered email and the in-app preview verbatim.
 *
 * Parsed rather than pattern-matched, because only a parser sees the value the way a
 * browser will: `&#106;avascript:` is a live scheme to a browser and inert text to a
 * regex, and `java<tab>script:` is that same scheme again.
 */

/** Tags a translation may never carry: they execute script or restyle the page around them. */
const FORBIDDEN_TAGS = new Set([
  "script",
  "iframe",
  "object",
  "embed",
  "style",
  "link",
  "meta",
  "base",
  "form",
  "svg",
  "math",
]);

const URL_ATTRIBUTES = new Set([
  "href",
  "src",
  "action",
  "formaction",
  "poster",
  "background",
  "data",
  "ping",
  "xlink:href",
]);

/**
 * A denylist because the hazard is script execution and that set is closed, while the
 * legitimate set is not — an app-install CTA points at `myapp://`, which an
 * http/https/mailto/tel allowlist would reject. `data:` is listed whole rather than by
 * media type, since `data:image/svg+xml` runs script exactly as `data:text/html` does.
 */
const SCRIPT_SCHEMES = new Set(["javascript", "data", "vbscript"]);

export function findUrlHazard(url: string): string | null {
  let scheme: string;
  try {
    // The WHATWG parser reads the scheme the way a browser does, dropping the tab and
    // control characters that make `java<tab>script:` navigate.
    scheme = new URL(url).protocol.slice(0, -1).toLowerCase();
  } catch {
    // Relative, a fragment, or a merge tag that resolves at send time — no scheme to reject.
    return null;
  }
  return SCRIPT_SCHEMES.has(scheme)
    ? `"${scheme}:" links aren't allowed in a translation`
    : null;
}

/**
 * The script-scheme denylist first, then an http(s) allowlist on top: the two reject
 * for unrelated reasons and word their messages differently in the upload ladder.
 * Relative image URLs render against the preview page origin and break in every mail
 * client, and a merge-tag-only cell has no scheme to allow until send time.
 */
export function findImageSrcHazard(url: string): string | null {
  return findAbsoluteHttpUrlHazard(url, "an image URL");
}

/** An SMS link has no page to resolve a relative URL against, so a relative one is dead for every recipient. */
export function findSmsLinkHazard(url: string): string | null {
  return findAbsoluteHttpUrlHazard(url, "a link");
}

/** A phone button dials its number as written, so it must be literal and in E.164 form. */
export function findPhoneNumberHazard(value: string): string | null {
  if (value.includes("{{") || value.includes("{%")) {
    return "a phone number can't contain variables";
  }
  return isE164(value.trim())
    ? null
    : "a phone number must be in E.164 format, like +15555550123";
}

function findAbsoluteHttpUrlHazard(url: string, noun: string): string | null {
  const urlHazard = findUrlHazard(url);
  if (urlHazard !== null) return urlHazard;
  try {
    const { protocol } = new URL(url);
    if (protocol === "http:" || protocol === "https:") return null;
  } catch {
    // Unlike `findUrlHazard`, relative and unparseable must fail the allowlist.
  }
  return `${noun} must start with http:// or https://`;
}

/**
 * A push image must be https like its source: the push editor refuses http, and
 * iOS App Transport Security and Android's cleartext policy block the fetch, so an
 * http translation would ship a push with no image and no error.
 */
export function findPushImageSrcHazard(url: string): string | null {
  return findHttpsImageSrcHazard(url, "a push image URL");
}

export function findHttpsImageSrcHazard(
  url: string,
  noun: string,
): string | null {
  const srcHazard = findImageSrcHazard(url);
  if (srcHazard !== null) return srcHazard;
  return url.trim().startsWith("https://")
    ? null
    : `${noun} must start with https://`;
}

/**
 * Not {@link isLocalizableUrl}: a URL that upload rejects would still be a required
 * translation row, with no value the marketer is allowed to type.
 */
export function isLocalizableImageSrc(url: string): boolean {
  return isLocalizableUrl(url) && findImageSrcHazard(url) === null;
}

/**
 * What closes a tag the value left open. `>` alone never reaches inside an open quote, so
 * `<a href="javascript:alert(1)` needs the quote closed first — the document around the
 * value supplies whichever of these the markup calls for.
 */
const TAG_TERMINATORS = [">", '">', "'>"];

/**
 * The appended terminator is what makes an unterminated tag visible: htmlparser2 discards a
 * dangling `<script src=x` at end of input, but the value is spliced INTO a document,
 * where the surrounding markup closes it. A bare `<` in prose ("5 < 10") still opens no tag.
 */
export function findHtmlHazards(value: string): string[] {
  const hazards = new Set<string>();
  for (const terminator of TAG_TERMINATORS) {
    for (const hazard of parseHazards(value + terminator)) {
      hazards.add(hazard);
    }
  }
  return [...hazards];
}

function parseHazards(markup: string): string[] {
  const hazards: string[] = [];
  const parser = new Parser({
    onopentag(name, attributes) {
      if (FORBIDDEN_TAGS.has(name)) {
        hazards.push(`<${name}> is not allowed in a translation`);
      }
      for (const [attribute, attributeValue] of Object.entries(attributes)) {
        if (attribute.startsWith("on")) {
          hazards.push(
            `<${name}> has an "${attribute}" event handler, which isn't allowed in a translation`,
          );
          continue;
        }
        if (!URL_ATTRIBUTES.has(attribute)) continue;
        const hazard = findUrlHazard(attributeValue);
        if (hazard !== null) {
          hazards.push(`<${name}> ${attribute}: ${hazard}`);
        }
      }
    },
  });
  parser.write(markup);
  parser.end();
  return hazards;
}

/** `{{ … }}` and `{% … %}`, which are resolved before the value reaches a document. */
const LIQUID_EXPRESSION_RE = /\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\}/g;

/**
 * An `href` or `alt` translation is written into an attribute the surrounding markup has
 * already opened, so a bare `"` in the value ends that attribute and turns the rest of the
 * cell into markup — `Hero" onerror="alert(1)` is a live event handler.
 */
export function findAttributeBreakout(value: string): string | null {
  return value.replace(LIQUID_EXPRESSION_RE, "").includes('"')
    ? "a double quote would end the attribute this value is written into, so write &quot; instead"
    : null;
}
